// Pure helpers for the Modern Trade invoice screens (spec 2026-11-03 §3):
// outright invoice per delivery (3A), consignment invoice by sale-date range
// (3B) and linking a payment to an invoice with an optional split amount.
// Vue-free so jest can cover them directly.
import { mtProductLabel } from "@/util/mt/productLabel";

const EPS = 0.005;

// Round to satang. The 1e-6 nudge keeps x.xx5 values (which binary floats
// store as x.xx4999…) rounding up the way a person would.
export function round2(v) {
  const n = Number(v);
  if (!isFinite(n)) return 0;
  return Math.round(n * 100 + 1e-6) / 100;
}

export function invoiceOutstanding(inv) {
  if (!inv) return 0;
  return Math.max(round2((Number(inv.net_amount) || 0) - (Number(inv.paid_amount) || 0)), 0);
}

// Link dialog default: as much of the payment as the invoice can still take.
export function defaultLinkAmount(paymentAmount, outstanding) {
  return Math.max(round2(Math.min(Number(paymentAmount) || 0, Number(outstanding) || 0)), 0);
}

// Returns the i18n leaf (under modern_trade.consignment_payment) describing why
// the amount cannot be linked, or null when it is fine.
export function linkAmountError(amount, paymentAmount, outstanding) {
  const a = Number(amount);
  if (amount === "" || amount === null || amount === undefined || !isFinite(a) || a <= 0) {
    return "link_amount_required";
  }
  if (a > (Number(paymentAmount) || 0) + EPS) return "link_amount_over_payment";
  if (a > (Number(outstanding) || 0) + EPS) return "link_amount_over_outstanding";
  return null;
}

// PUT /payments/:id/link body. `amount` is sent only when the payment is split,
// so a full link looks exactly like it did before the split feature.
export function linkPayload(invoiceId, amount, paymentAmount) {
  const body = { invoice_id: invoiceId };
  if (round2(amount) < round2(paymentAmount) - EPS) body.amount = round2(amount);
  return body;
}

// "ออกใบกำกับภาษี" button on a delivery row (3A). GET /sale-orders/:id decorates
// every delivery row with a boolean can_issue_from_delivery
// (mtSaleOrderDeliveryRows, MT-InvoiceFromDelivery.go); the button needs it to
// be exactly true. The server's 409 codes (delivery_already_invoiced /
// has_source3_invoice / not_outright) stay the final guard; the local checks
// only keep the button off sooner when the page state already says no.
export function canIssueFromDelivery(row, { canMutate, saleType, paymentCap } = {}) {
  if (!canMutate || !row) return false;
  if (row.invoice_id) return false;
  if (row.status === 9) return false;
  if (saleType !== 2) return false;
  if (paymentCap && paymentCap.has_source3) return false;
  return row.can_issue_from_delivery === true;
}

// A delivery carrying invoice_id means the SO already runs on the
// from-delivery flow (cancelling the invoice resets invoice_id to NULL).
export function hasFromDeliveryInvoice(deliveries) {
  return (deliveries || []).some(d => d && Number(d.invoice_id) > 0);
}

// Legacy "issue from payments" (source 3) button: hidden once the SO has a
// from-delivery invoice, otherwise as the server's can_issue_invoice says.
// 2026-11-04 (owner): retired for outright (sale_type 2) SOs that are not
// legacy — only an SO already on the source-3 flow (payment_cap.has_source3)
// keeps it; every other outright SO invoices per delivery note. Same rule as
// mtSaleOrderMoneyInvoiceAllowed (bo-api), which also drives can_issue_invoice;
// checked here too so the button stays hidden against an older API.
export function showSource3IssueButton(paymentCap, deliveries, saleType) {
  if (!paymentCap || !paymentCap.can_issue_invoice) return false;
  if (hasFromDeliveryInvoice(deliveries)) return false;
  if (Number(saleType) === 2 && paymentCap.has_source3 !== true) return false;
  return true;
}

// BE error code (go-core Msg) → i18n key. Written out in full so i18n checks
// can grep every key.
export function invoiceErrorKey(code) {
  switch (code) {
    case "delivery_already_invoiced": return "modern_trade.invoice.err_delivery_already_invoiced";
    case "has_source3_invoice": return "modern_trade.invoice.err_has_source3_invoice";
    case "nothing_to_bill": return "modern_trade.invoice.err_nothing_to_bill";
    case "unpriced_sales": return "modern_trade.invoice.err_unpriced_sales";
    case "not_outright": return "modern_trade.invoice.err_not_outright";
    case "use_delivery_invoice": return "modern_trade.invoice.err_use_delivery_invoice";
    case "use_sales_range_invoice": return "modern_trade.invoice.err_use_sales_range_invoice";
    case "invoice_exists": return "modern_trade.invoice.err_invoice_exists";
    case "discount_exceeds_subtotal": return "modern_trade.invoice.err_discount_exceeds_subtotal";
    case "delivery_cancelled": return "modern_trade.invoice.err_delivery_cancelled";
    case "sale_order_closed": return "modern_trade.invoice.err_sale_order_closed";
    // go-core code is "state_changed" (mtCodePaymentStateChanged)
    case "state_changed": return "modern_trade.invoice.err_payment_state_changed";
    case "preview_stale": return "modern_trade.invoice.err_preview_stale";
    default: return null;
  }
}

export function invoiceSourceKey(source) {
  switch (Number(source)) {
    case 1: return "modern_trade.invoice.source_outright";
    case 2: return "modern_trade.invoice.source_consignment";
    case 3: return "modern_trade.invoice.source_payment";
    default: return null;
  }
}

// "ออกใบ" stays disabled until a preview with at least one line and no
// unpriced product is on screen.
export function rangePreviewBlocked(preview) {
  if (!preview) return true;
  const lines = preview.lines || [];
  const unpriced = preview.unpriced || [];
  return lines.length === 0 || unpriced.length > 0;
}

// POST /consignment-invoices/range body: exactly the params the preview on
// screen was built from (never the live form, which may have changed since),
// plus expected_net so the BE answers 409 preview_stale when the totals moved.
// null when there is no usable preview.
export function rangeIssuePayload(preview) {
  const p = preview && preview.params;
  if (!p || !p.company_id || !p.from || !p.to) return null;
  return { company_id: p.company_id, from: p.from, to: p.to, expected_net: round2(preview.net) };
}

// Unpriced entries may come back as product rows or as bare ids/codes.
export function unpricedLabel(u) {
  if (u === null || u === undefined) return "";
  if (typeof u !== "object") return String(u);
  const text = mtProductLabel(u).text;
  if (text) return text;
  if (u.wms_product_id) return "wms#" + u.wms_product_id;
  if (u.product_id) return "#" + u.product_id;
  return "";
}
